import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const reset = document.createElement('style');
reset.textContent = [
  /* ── Responsive layout ────────────────────────────────────────────────
     Stripe-style reflow: the shell is fluid at every width. Below 760px the
     `@media (max-width:760px)` rules collapse grids/rows to a single column
     so nothing is clipped — same DOM, same text size, layout just stacks. */
  /* body is the scroll container (html is locked) so the page reliably
     scrolls BOTH ways: horizontally to reveal the full 1280 desktop width,
     vertically for tall pages. A plain wide body under an overflow:visible
     html does NOT scroll horizontally in some engines, hence this recipe. */
  'html{margin:0;padding:0;height:100%;overflow:hidden}',
  'body{margin:0;padding:0;height:100%;overflow:auto;-webkit-overflow-scrolling:touch}',
  '#root{min-height:100%}',
  'html,body{-webkit-text-size-adjust:100%}',
  '*{box-sizing:border-box}',
  'img,video,svg{max-width:100%;height:auto}',
  'input,select,textarea,button{font-family:inherit}',
  /* keep iOS from zooming on focus (needs >=16px font on inputs) handled per-field */
  '*{scrollbar-width:thin;scrollbar-color:transparent transparent}',
  '::-webkit-scrollbar{width:6px;height:6px}',
  '::-webkit-scrollbar-track{background:transparent}',
  '::-webkit-scrollbar-thumb{background:transparent;border-radius:8px;transition:background .25s ease}',
  'html.is-scrolling *{scrollbar-color:rgba(0,0,0,0.25) transparent}',
  'html.is-scrolling ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.25)}',
  'html.is-scrolling ::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,0.4)}',
  /* responsive helpers used across pages */
  '.hm-hide-mobile{display:initial}',
  '.hm-only-mobile{display:none}',
  /* ── Horizontal-scroll convention ──────────────────────────────────
     The app keeps its responsive (collapse-to-column) mobile layout. For
     blocks that must NOT collapse — wide tables, document/invoice grids,
     steppers — wrap them so they scroll sideways on small screens instead
     of being clipped or crushed:
       <div class="hm-scroll-x"><div class="hm-scroll-x-inner">…wide…</div></div>
     `.hm-scroll-x` is the scroll viewport; the inner sizes to its content
     (max-content) so it fits on desktop and scrolls on mobile. For CSS-grid
     tables whose flexible column would explode under max-content, skip the
     inner class and give the inner div an explicit `min-width` instead. */
  '.hm-scroll-x{overflow-x:auto;-webkit-overflow-scrolling:touch;max-width:100%}',
  '.hm-scroll-x>.hm-scroll-x-inner{min-width:max-content}',
  '@media (max-width:760px){',
  '  .hm-hide-mobile{display:none !important}',
  '  .hm-only-mobile{display:inline-flex !important}',
  '  .hm-grid-2,.hm-grid-3,.hm-grid-4{grid-template-columns:1fr !important}',
  '  .hm-row-mobile-col{flex-direction:column !important;align-items:stretch !important}',
  /* Inline-styled grids → collapse on small screens */
  '  [style*="grid-template-columns: 1fr 1fr"]:not([style*="auto-fit"]):not([style*="auto-fill"]),',
  '  [style*="grid-template-columns:1fr 1fr"]:not([style*="auto-fit"]):not([style*="auto-fill"]),',
  '  [style*="grid-template-columns: 1fr 1fr 1fr"],',
  '  [style*="grid-template-columns: repeat(3"]:not([style*="auto-fit"]):not([style*="auto-fill"]),',
  '  [style*="grid-template-columns: repeat(4"]:not([style*="auto-fit"]):not([style*="auto-fill"]),',
  '  [style*="grid-template-columns: repeat(6"]:not([style*="auto-fit"]):not([style*="auto-fill"]),',
  '  [style*="grid-template-columns: 2fr"],',
  '  [style*="minmax(0px, 2fr)"],',
  '  [style*="minmax(0, 2fr)"],',
  '  [style*="minmax(0px, 1fr) minmax(0px, 1fr)"],',
  '  [style*="minmax(0, 1fr) minmax(0, 1fr)"],',
  '  [style*="minmax(0px, 1fr) 160px"],',
  '  [style*="minmax(0, 1fr) 160px"],',
  '  [style*="grid-template-columns: 230px"],',
  '  [style*="grid-template-columns: 300px"],',
  '  [style*="grid-template-columns: 260px"],',
  '  [style*="grid-template-columns: 240px"]{grid-template-columns:1fr !important}',
  /* Modal max-width respects small viewports */
  '  .hm-modal-mobile{max-width:100% !important;border-radius:10px !important}',
  /* SplitDetail (enquiry detail header) — wrap action tabs onto own row, horizontally scrollable */
  '  .hm-split-head{flex-wrap:wrap !important;gap:8px !important}',
  '  .hm-split-title{font-size:16px !important}',
  '  .hm-split-actions{order:99;flex-basis:100%;width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:4px;scrollbar-width:none}',
  '  .hm-split-actions::-webkit-scrollbar{display:none}',
  '  .hm-split-actions > button{flex-shrink:0 !important;padding:8px 14px !important;font-size:12.5px !important}',
  /* Detail panel (SplitDetail): wide documents (quote/WO/invoice/logbook)
     don't collapse below their natural width, so scroll them sideways here
     instead of clipping — lets mobile users read the full PC-width doc. */
  '  .hm-panelin{overflow-x:auto;-webkit-overflow-scrolling:touch}',
  '  .hm-content > *{max-width:100%}',
  /* EnquiryDetailsView: hide table header, stack each row as a card with label/value pairs */
  '  .enq-thead{display:none !important}',
  '  .enq-mrow{display:block !important;padding:14px 16px !important;position:relative}',
  '  .enq-mrow .enq-num{position:absolute;top:14px;right:16px}',
  '  .enq-mrow > div[data-lbl]{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:4px 0;text-align:left !important;font-size:13px}',
  '  .enq-mrow > div[data-lbl]::before{content:attr(data-lbl);color:#94a3b8;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;flex-shrink:0;padding-top:2px}',
  '  .enq-mrow > div[data-lbl="Machine"]{font-weight:700;font-size:14px;padding-bottom:8px;border-bottom:1px solid #e2e8f0;margin-bottom:6px;padding-right:34px}',
  '  .enq-mrow > div[data-lbl="Machine"]::before{display:none}',
  /* ChatPane: on mobile show only contacts OR thread, with a back button to return. */
  '  .hm-chat-pane.is-active .hm-chat-sidebar{display:none !important}',
  '  .hm-chat-pane:not(.is-active) .hm-chat-thread{display:none !important}',
  '  .hm-chat-pane.is-active .hm-chat-back{display:flex !important}',
  '  .hm-chat-pane.is-active .hm-chat-thread > *:nth-child(2) > div:first-child{padding-left:42px !important}',
  '}'
].join('');
document.head.appendChild(reset);

let scrollHideTimer;
document.addEventListener('scroll', () => {
  document.documentElement.classList.add('is-scrolling');
  clearTimeout(scrollHideTimer);
  scrollHideTimer = setTimeout(() => {
    document.documentElement.classList.remove('is-scrolling');
  }, 900);
}, true);

ReactDOM.createRoot(document.getElementById('root')).render(<React.StrictMode><App /></React.StrictMode>);
